import { DeleteOutlined, EditOutlined, PictureOutlined, PlusOutlined } from '@ant-design/icons'
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Empty,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Select,
  Skeleton,
  Switch,
  Tag,
  Typography,
  Upload,
  message,
} from 'antd'
import type { UploadFile } from 'antd/es/upload/interface'
import { useCallback, useEffect, useState } from 'react'
import {
  createSiteEntry,
  deleteSiteEntry,
  listContentTypeFields,
  listContentTypes,
  listSiteEntries,
  listSiteTaxonomies,
  listTaxonomyTerms,
  siteAssetVariantUrl,
  updateSiteEntry,
  uploadSiteAsset,
} from '../../api'
import type { SaasApiError } from '../../api'
import type { ContentEntry, FieldDefinitionOut, Taxonomy, TaxonomyTerm } from '../../types'

const { Title, Text, Paragraph } = Typography

function slugify(value: string): string {
  return value.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')
}

export interface EntryFieldSpec {
  key: string
  label: string
  placeholder?: string
  type?: 'text' | 'textarea'
  required?: boolean
}

export interface EntryManagerTabProps {
  workspaceId: string
  siteId: string
  contentType: string
  entityLabel: string
  entityLabelPlural: string
  /** Extra `fields` (beyond `description`) shown in the create/edit form — this is where LiangCollection's
   * per-content-type custom-field shape (material/era/venue/author/...) surfaces (spec §11).
   * Ignored when `dynamicSchema` is true. */
  fields: EntryFieldSpec[]
  /** Taxonomy keys ("category"/"topic") to show as tag checkboxes — only Items use this. */
  taxonomyKeys?: string[]
  /** Show a multi-select of existing Items, stored at `fields.item_ids` — Exhibition/Research use this
   * to reference Items (spec's "Item 关联"). */
  withItemPicker?: boolean
  /** Generic Content Schema Engine (Phase A2, spec §22) — when true, the create/edit form's custom
   * fields are fetched from `GET .../content-types/{id}/fields` (keyed by `contentType`) and rendered
   * generically by `field_type`, instead of the static `fields` prop above. A new field added through
   * the "Content Models" admin tab shows up here on next open — no React source change required. */
  dynamicSchema?: boolean
}

/**
 * Generic Content-Entry CRUD surface shared by Items / Exhibitions /
 * Research (LiangCollection Full Website Absorption). One component,
 * parameterized by `contentType` + field spec, rather than three
 * hand-copied CRUD pages — Vistoera Core stays content-type-agnostic
 * while each LiangCollection capability still gets its own tab with its
 * own real fields/taxonomy/item-reference behaviour.
 */
export default function EntryManagerTab({
  workspaceId,
  siteId,
  contentType,
  entityLabel,
  entityLabelPlural,
  fields,
  taxonomyKeys = [],
  withItemPicker = false,
  dynamicSchema = false,
}: EntryManagerTabProps) {
  const [entries, setEntries] = useState<ContentEntry[] | null>(null)
  const [allItems, setAllItems] = useState<ContentEntry[]>([])
  const [taxonomies, setTaxonomies] = useState<Array<{ taxonomy: Taxonomy; terms: TaxonomyTerm[] }>>([])
  const [error, setError] = useState<string | null>(null)
  const [modalOpen, setModalOpen] = useState(false)
  const [editing, setEditing] = useState<ContentEntry | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [fileList, setFileList] = useState<UploadFile[]>([])
  const [selectedTermIds, setSelectedTermIds] = useState<string[]>([])
  const [form] = Form.useForm()

  // ---------------------------------------- Generic Content Schema (A2)
  const [dynamicFields, setDynamicFields] = useState<FieldDefinitionOut[]>([])
  const [assetFieldFiles, setAssetFieldFiles] = useState<Record<string, UploadFile[]>>({})
  const [assetFieldExisting, setAssetFieldExisting] = useState<Record<string, string | null>>({})
  const [referenceOptions, setReferenceOptions] = useState<Record<string, ContentEntry[]>>({})

  useEffect(() => {
    if (!dynamicSchema) return
    listContentTypes(workspaceId, siteId).then((r) => {
      const ct = r.data.find((c) => c.key === contentType)
      if (!ct) return
      listContentTypeFields(workspaceId, siteId, ct.id).then((fr) => {
        const active = fr.data.filter((f) => f.status === 'active' && f.editor_visibility).sort((a, b) => a.sort_order - b.sort_order)
        setDynamicFields(active)
        const refFields = active.filter((f) => f.field_type === 'entry_reference')
        refFields.forEach((f) => {
          const targetType = (f.config?.content_type as string | undefined) || undefined
          listSiteEntries(workspaceId, siteId, targetType).then((er) => {
            setReferenceOptions((prev) => ({ ...prev, [f.key]: er.data }))
          })
        })
      })
    })
  }, [workspaceId, siteId, contentType, dynamicSchema])

  const load = useCallback(() => {
    setError(null)
    listSiteEntries(workspaceId, siteId, contentType)
      .then((r) => setEntries(r.data))
      .catch((err) => setError((err as SaasApiError).message))
  }, [workspaceId, siteId, contentType])

  useEffect(load, [load])

  useEffect(() => {
    if (withItemPicker) {
      listSiteEntries(workspaceId, siteId, 'item').then((r) => setAllItems(r.data))
    }
  }, [workspaceId, siteId, withItemPicker])

  useEffect(() => {
    if (taxonomyKeys.length === 0) return
    listSiteTaxonomies(workspaceId, siteId).then((r) => {
      const relevant = r.data.filter((t) => taxonomyKeys.includes(t.key))
      Promise.all(relevant.map((t) => listTaxonomyTerms(workspaceId, siteId, t.id).then((tr) => ({ taxonomy: t, terms: tr.data })))).then(
        setTaxonomies,
      )
    })
  }, [workspaceId, siteId, taxonomyKeys.join(',')])

  const openCreate = () => {
    setEditing(null)
    form.resetFields()
    setFileList([])
    setSelectedTermIds([])
    setAssetFieldFiles({})
    setAssetFieldExisting({})
    setModalOpen(true)
  }

  const openEdit = (entry: ContentEntry) => {
    setEditing(entry)
    const dynamicValues: Record<string, unknown> = {}
    const existingAssets: Record<string, string | null> = {}
    if (dynamicSchema) {
      for (const f of dynamicFields) {
        if (f.field_type === 'asset') {
          existingAssets[f.key] = (entry.fields[f.key] as string | undefined) ?? null
        } else {
          dynamicValues[f.key] = entry.fields[f.key] ?? (f.field_type === 'multi_select' ? [] : f.field_type === 'boolean' ? false : '')
        }
      }
    }
    form.setFieldsValue({
      title: entry.label,
      slug: entry.slug,
      ...(dynamicSchema ? dynamicValues : Object.fromEntries(fields.map((f) => [f.key, entry.fields[f.key] ?? '']))),
      item_ids: (entry.fields.item_ids as string[] | undefined) ?? [],
    })
    setFileList([])
    setAssetFieldFiles({})
    setAssetFieldExisting(existingAssets)
    setSelectedTermIds(entry.taxonomy_term_ids)
    setModalOpen(true)
  }

  const onSubmit = async () => {
    const values = await form.validateFields()
    setSubmitting(true)
    try {
      let coverAssetId: string | null = editing?.cover_asset_id ?? null
      const file = fileList[0]?.originFileObj
      if (file) {
        const { data: asset } = await uploadSiteAsset(workspaceId, siteId, file)
        coverAssetId = asset.id
      }
      // `updateSiteEntry`/`createSiteEntry` replace `fields` wholesale (no
      // partial-merge semantics — see `content_entry_service.update_fields`'s
      // docstring), so any pre-existing key this ContentType has no active
      // FieldDefinition for (e.g. `description`, the LiangCollection theme's
      // detail-page subtitle slot — deliberately never backfilled as a
      // generic field, see `scripts/backfill_field_definitions_from_entries.py`)
      // must be carried forward from `editing.fields` here, or saving an
      // Item through the Dynamic Item Editor would silently drop it (spec
      // §23: "不要破坏现有 Item 编辑功能"). A dynamic field itself is
      // explicitly `delete`d (not left stale) when cleared in the UI, so
      // clearing a Select/Input back to empty actually clears the stored
      // value instead of silently keeping the old one.
      const extraFields: Record<string, unknown> = dynamicSchema && editing ? { ...editing.fields } : {}
      if (dynamicSchema) {
        for (const f of dynamicFields) {
          if (f.field_type === 'asset') {
            const assetFile = assetFieldFiles[f.key]?.[0]?.originFileObj
            if (assetFile) {
              const { data: uploaded } = await uploadSiteAsset(workspaceId, siteId, assetFile)
              extraFields[f.key] = uploaded.id
            } else if (assetFieldExisting[f.key]) {
              extraFields[f.key] = assetFieldExisting[f.key]
            } else {
              delete extraFields[f.key]
            }
            continue
          }
          const raw = values[f.key]
          const isEmpty = raw === undefined || raw === '' || raw === null || (Array.isArray(raw) && raw.length === 0)
          if (isEmpty) {
            delete extraFields[f.key]
            continue
          }
          extraFields[f.key] = raw
        }
      } else {
        for (const f of fields) extraFields[f.key] = values[f.key] ?? ''
      }
      if (withItemPicker) extraFields.item_ids = values.item_ids ?? []

      if (editing) {
        await updateSiteEntry(workspaceId, siteId, editing.id, {
          label: values.title.trim(),
          fields: extraFields,
          cover_asset_id: coverAssetId,
          taxonomy_term_ids: selectedTermIds,
        })
        message.success(`「${values.title}」已更新`)
      } else {
        await createSiteEntry(workspaceId, siteId, {
          content_type: contentType,
          slug: (values.slug || slugify(values.title)).trim(),
          label: values.title.trim(),
          fields: extraFields,
          status: 'published',
          cover_asset_id: coverAssetId,
          taxonomy_term_ids: selectedTermIds,
        })
        message.success(`「${values.title}」已创建`)
      }
      setModalOpen(false)
      load()
    } catch (err) {
      message.error((err as SaasApiError).message)
    } finally {
      setSubmitting(false)
    }
  }

  const onDelete = async (entry: ContentEntry) => {
    try {
      await deleteSiteEntry(workspaceId, siteId, entry.id)
      message.success(`「${entry.label}」已删除`)
      load()
    } catch (err) {
      message.error((err as SaasApiError).message)
    }
  }

  if (error) return <Alert type="error" message={error} showIcon style={{ marginTop: 16 }} />
  if (entries === null) return <Skeleton active paragraph={{ rows: 4 }} style={{ marginTop: 16 }} />

  const CoverImage = ({ assetId, alt }: { assetId: string | null; alt: string }) =>
    assetId ? (
      <img
        src={siteAssetVariantUrl(workspaceId, assetId, 'thumbnail')}
        alt={alt}
        style={{ width: '100%', aspectRatio: '4/3', objectFit: 'cover', display: 'block', background: '#f0f0f0' }}
      />
    ) : (
      <div
        style={{ width: '100%', aspectRatio: '4/3', background: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#bbb' }}
      >
        <PictureOutlined style={{ fontSize: 28 }} />
      </div>
    )

  return (
    <div style={{ marginTop: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={5} style={{ margin: 0 }}>
          {entityLabelPlural}
        </Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
          新建{entityLabel}
        </Button>
      </div>
      {entries.length === 0 ? (
        <Empty description={`暂无${entityLabelPlural}`} style={{ marginTop: 60 }}>
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
            新建{entityLabel}
          </Button>
        </Empty>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 16 }}>
          {entries.map((entry) => (
            <Card
              key={entry.id}
              cover={<CoverImage assetId={entry.cover_asset_id} alt={entry.label} />}
              actions={[
                <EditOutlined key="edit" onClick={() => openEdit(entry)} />,
                <Popconfirm key="del" title={`确认删除「${entry.label}」？`} onConfirm={() => onDelete(entry)}>
                  <DeleteOutlined />
                </Popconfirm>,
              ]}
            >
              <Card.Meta title={entry.label} description={String(entry.fields.description || '')} />
              <div style={{ marginTop: 8 }}>
                <Tag color={entry.status === 'published' ? 'green' : 'default'}>
                  {entry.status === 'published' ? '已发布' : entry.status === 'archived' ? '已归档' : '草稿'}
                </Tag>
                {entry.taxonomy_term_ids.length > 0 && <Tag color="blue">{entry.taxonomy_term_ids.length} 个标签</Tag>}
              </div>
            </Card>
          ))}
        </div>
      )}

      <Modal
        title={editing ? `编辑${entityLabel}` : `新建${entityLabel}`}
        open={modalOpen}
        onOk={onSubmit}
        onCancel={() => setModalOpen(false)}
        okText={editing ? '保存' : '创建'}
        cancelText="取消"
        confirmLoading={submitting}
        width={640}
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Form.Item name="title" label="标题" rules={[{ required: true, message: '请输入标题' }]}>
            <Input />
          </Form.Item>
          {!editing && (
            <Form.Item name="slug" label="内部 slug" tooltip="URL 标识，留空则根据标题自动生成">
              <Input placeholder="留空自动生成" />
            </Form.Item>
          )}
          {!dynamicSchema &&
            fields.map((f) => (
              <Form.Item key={f.key} name={f.key} label={f.label} rules={f.required ? [{ required: true, message: `请输入${f.label}` }] : []}>
                {f.type === 'textarea' ? <Input.TextArea rows={3} placeholder={f.placeholder} /> : <Input placeholder={f.placeholder} />}
              </Form.Item>
            ))}
          {dynamicSchema &&
            dynamicFields.map((f) => {
              const rules = f.required ? [{ required: true, message: `请输入${f.label}` }] : []
              if (f.field_type === 'asset') {
                const currentFiles = assetFieldFiles[f.key] ?? []
                const existingId = assetFieldExisting[f.key]
                return (
                  <Form.Item key={f.key} label={f.label} help={f.help_text || undefined}>
                    {existingId && currentFiles.length === 0 && (
                      <img
                        src={siteAssetVariantUrl(workspaceId, existingId, 'thumbnail')}
                        alt={f.label}
                        style={{ width: 120, height: 90, objectFit: 'cover', borderRadius: 4, marginBottom: 8, display: 'block' }}
                      />
                    )}
                    <Upload
                      listType="picture"
                      maxCount={1}
                      fileList={currentFiles}
                      beforeUpload={() => false}
                      onChange={({ fileList: next }) => setAssetFieldFiles((prev) => ({ ...prev, [f.key]: next.slice(-1) }))}
                      accept="image/*"
                    >
                      <Button icon={<PictureOutlined />}>{existingId ? '替换图片' : '上传图片'}</Button>
                    </Upload>
                  </Form.Item>
                )
              }
              if (f.field_type === 'entry_reference') {
                return (
                  <Form.Item key={f.key} name={f.key} label={f.label} help={f.help_text || undefined} rules={rules}>
                    <Select
                      allowClear
                      placeholder="请选择关联内容"
                      options={(referenceOptions[f.key] ?? []).map((e) => ({ label: e.label, value: e.id }))}
                      optionFilterProp="label"
                    />
                  </Form.Item>
                )
              }
              if (f.field_type === 'select') {
                return (
                  <Form.Item key={f.key} name={f.key} label={f.label} help={f.help_text || undefined} rules={rules}>
                    <Select allowClear options={f.options.map((o) => ({ label: o.label || o.value, value: o.value }))} />
                  </Form.Item>
                )
              }
              if (f.field_type === 'multi_select') {
                return (
                  <Form.Item key={f.key} name={f.key} label={f.label} help={f.help_text || undefined} rules={rules}>
                    <Select mode="multiple" allowClear options={f.options.map((o) => ({ label: o.label || o.value, value: o.value }))} />
                  </Form.Item>
                )
              }
              if (f.field_type === 'boolean') {
                return (
                  <Form.Item key={f.key} name={f.key} label={f.label} help={f.help_text || undefined} valuePropName="checked">
                    <Switch />
                  </Form.Item>
                )
              }
              if (f.field_type === 'number') {
                return (
                  <Form.Item key={f.key} name={f.key} label={f.label} help={f.help_text || undefined} rules={rules}>
                    <InputNumber style={{ width: '100%' }} />
                  </Form.Item>
                )
              }
              if (f.field_type === 'date') {
                return (
                  <Form.Item key={f.key} name={f.key} label={f.label} help={f.help_text || undefined} rules={rules}>
                    <Input type="date" />
                  </Form.Item>
                )
              }
              if (f.field_type === 'textarea' || f.field_type === 'rich_text') {
                return (
                  <Form.Item key={f.key} name={f.key} label={f.label} help={f.help_text || undefined} rules={rules}>
                    <Input.TextArea rows={3} />
                  </Form.Item>
                )
              }
              // text / url
              return (
                <Form.Item key={f.key} name={f.key} label={f.label} help={f.help_text || undefined} rules={rules}>
                  <Input placeholder={f.field_type === 'url' ? 'https://...' : undefined} />
                </Form.Item>
              )
            })}
          {taxonomies.map(({ taxonomy, terms }) => (
            <Form.Item key={taxonomy.id} label={taxonomy.name} style={{ marginBottom: 8 }}>
              {terms.length === 0 ? (
                <Text type="secondary">暂无{taxonomy.name}，请先在「条目分类」中添加。</Text>
              ) : (
                <Checkbox.Group
                  value={selectedTermIds.filter((id) => terms.some((t) => t.id === id))}
                  options={terms.map((t) => ({ label: t.name, value: t.id }))}
                  onChange={(checked) => {
                    const otherTaxonomyIds = selectedTermIds.filter((id) => !terms.some((t) => t.id === id))
                    setSelectedTermIds([...otherTaxonomyIds, ...(checked as string[])])
                  }}
                />
              )}
            </Form.Item>
          ))}
          {withItemPicker && (
            <Form.Item name="item_ids" label="关联条目">
              <Select
                mode="multiple"
                placeholder="选择要在此条目中展示的条目"
                options={allItems.map((it) => ({ label: it.label, value: it.id }))}
                optionFilterProp="label"
              />
            </Form.Item>
          )}
          <Form.Item label="封面图片">
            <Upload
              listType="picture-card"
              fileList={fileList}
              beforeUpload={() => false}
              onChange={({ fileList: next }) => setFileList(next.slice(-1))}
              maxCount={1}
              accept="image/*"
            >
              {fileList.length === 0 && (
                <span>
                  <PictureOutlined />
                  <div style={{ marginTop: 8 }}>上传</div>
                </span>
              )}
            </Upload>
            {editing?.cover_asset_id && fileList.length === 0 && (
              <Paragraph type="secondary" style={{ fontSize: 12, marginTop: 4 }}>
                未上传新图片时将保留原封面。
              </Paragraph>
            )}
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}
